type StoredEventListener =
  | EventListenerOrEventListenerObject
  | ((event: StorageEvent) => void)

const eventListenerMap = new Map<string, StoredEventListener>()

interface BaseStoredEventListenerProps<Key = string> {
  /** The target element that you want to add the event listener to */
  target: Window | Document | HTMLElement

  /**
   * The event type
   * @example "click"
   */
  type: string

  /**
   * The `key` of the listener so we can target the event for removal
   */
  key: Key

  /**
   * The event listener
   */
  listener: StoredEventListener

  /**
   * The options for the `addEventListener` method
   */
  options?: boolean | AddEventListenerOptions
}

/**
 * Adds an event listener and stores it, to make it easy to remove it later, for example in separate files where it
 * would be cumbersome to export the listener function
 */
export const addStoredEventListener = <Key = string>({
  target,
  type,
  key,
  listener,
  options,
}: BaseStoredEventListenerProps<Key>) => {
  eventListenerMap.set(`${key}_${type}`, listener)
  target.addEventListener(
    type,
    listener as EventListenerOrEventListenerObject,
    options
  )
}

/**
 * Removes an event listener that has been previously set using `addStoredEventListener`
 */
export const removeStoredEventListener = <Key = string>({
  target,
  type,
  key,
  options,
}: Omit<BaseStoredEventListenerProps<Key>, 'listener'>) => {
  const listener = eventListenerMap.get(`${key}_${type}`)

  if (listener) {
    target.removeEventListener(
      type,
      listener as EventListenerOrEventListenerObject,
      options
    )
    eventListenerMap.delete(`${key}_${type}`)
  }
}
